import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RuleIcon from '@mui/icons-material/Rule';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ReplayIcon from '@mui/icons-material/Replay';

import { useAuth } from '../../context/AuthContext';
import paymentsApi from '../../services/paymentsApi';
import BatchStatusChip from '../../components/payments/BatchStatusChip';
import ReviewGrid from '../../components/payments/ReviewGrid';
import ExceptionQueue from '../../components/payments/ExceptionQueue';
import ExcludeRowDialog from '../../components/payments/ExcludeRowDialog';
import MappingDialog from '../../components/payments/MappingDialog';
import SubmitToFinanceDialog from '../../components/payments/SubmitToFinanceDialog';
import FinanceApproveDialog from '../../components/payments/FinanceApproveDialog';
import FinanceDecisionDialog from '../../components/payments/FinanceDecisionDialog';
import AdjustmentDialog from '../../components/payments/AdjustmentDialog';

const EDITABLE_STATUSES = ['uploaded', 'imported', 'operations_review', 'draft', 'returned_to_operations'];
const FINANCE_ACTIONABLE_STATUSES = ['submitted_to_finance', 'finance_review'];
const APPROVED_OR_LATER = ['finance_approved', 'emails_pending_approval', 'emails_approved', 'emails_sending', 'emails_sent', 'qbo_export_pending', 'qbo_exported', 'closed'];
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function StatCard({ label, value }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
      <Typography variant="h5">{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Paper>
  );
}

function BatchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();

  const [batch, setBatch] = useState(null);
  const [rows, setRows] = useState([]);
  const [ruleResults, setRuleResults] = useState([]);
  const [providerTotals, setProviderTotals] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [statements, setStatements] = useState([]);
  const [qboExports, setQboExports] = useState([]);
  const [auditEvents, setAuditEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState(0);
  const [notes, setNotes] = useState('');
  const [selection, setSelection] = useState([]);
  const [excludeTarget, setExcludeTarget] = useState(null);
  const [mapping, setMapping] = useState(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [decision, setDecision] = useState(null); // { kind, title, description, confirmLabel, target }
  const [adjustmentOpen, setAdjustmentOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });
  const editable = batch && EDITABLE_STATUSES.includes(batch.status);
  const financeActionable = batch && hasRole('finance') && FINANCE_ACTIONABLE_STATUSES.includes(batch.status);
  const approvedOrLater = batch && APPROVED_OR_LATER.includes(batch.status);
  const canGenerateStatements = approvedOrLater && hasRole('finance');

  const loadBatch = useCallback(async () => {
    const b = await paymentsApi.getBatch(id);
    setBatch(b);
    setNotes(b.notes || '');
    return b;
  }, [id]);
  const loadRows = useCallback(async () => {
    const data = await paymentsApi.getRows(id, { limit: 1000 });
    setRows(data.rows);
    return data.rows;
  }, [id]);
  const loadRuleResults = useCallback(async () => setRuleResults(await paymentsApi.getRuleResults(id)), [id]);
  const loadProviderTotals = useCallback(async () => setProviderTotals(await paymentsApi.getProviderTotals(id)), [id]);
  const loadAdjustments = useCallback(async () => setAdjustments(await paymentsApi.listAdjustments(id)), [id]);
  const loadApprovals = useCallback(async () => setApprovals(await paymentsApi.listApprovals(id)), [id]);
  const loadStatements = useCallback(async () => setStatements(await paymentsApi.listStatements(id)), [id]);
  const loadQboExports = useCallback(async () => setQboExports(await paymentsApi.listQboExports(id)), [id]);
  const loadAudit = useCallback(async () => setAuditEvents(await paymentsApi.getAuditEvents(id)), [id]);

  const refreshDerived = useCallback(async () => {
    await Promise.all([loadBatch(), loadRuleResults(), loadProviderTotals(), loadAdjustments(), loadApprovals()]);
  }, [loadBatch, loadRuleResults, loadProviderTotals, loadAdjustments, loadApprovals]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await Promise.all([loadBatch(), loadRows(), loadRuleResults(), loadProviderTotals(), loadAdjustments(), loadApprovals(), loadStatements(), loadQboExports()]);
      } catch (e) {
        notify(e.message, 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadBatch, loadRows, loadRuleResults, loadProviderTotals, loadAdjustments, loadApprovals, loadStatements, loadQboExports]);

  // ---- Operations mutations ----
  const handleCommitEdit = async (rowId, fields) => {
    const res = await paymentsApi.patchRow(rowId, { fields });
    const fresh = await loadRows();
    await refreshDerived();
    notify('Row updated and batch recalculated.');
    return fresh.find((r) => r.id === rowId) || res.row;
  };

  const runMutation = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      await loadRows();
      await refreshDerived();
      if (successMsg) notify(successMsg);
    } catch (e) {
      notify(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const doExclude = ({ reason, note }) =>
    runMutation(async () => { await paymentsApi.excludeRow(excludeTarget.id, { reason, note }); setExcludeTarget(null); }, 'Row excluded.');
  const handleInclude = (row) => runMutation(() => paymentsApi.includeRow(row.id), 'Row re-included.');
  const doMapping = ({ targetId }) =>
    runMutation(async () => {
      if (mapping.type === 'provider') await paymentsApi.createProviderMapping({ uploadedName: mapping.row.uploaded_provider_name, providerId: targetId, batchId: id });
      else await paymentsApi.createServiceTypeMapping({ uploadedValue: mapping.row.uploaded_service_type, serviceTypeId: targetId, orderSource: batch.order_source, batchId: id });
      setMapping(null);
    }, 'Mapping created and batch recalculated.');
  const handleResolve = (rrId) => runMutation(() => paymentsApi.resolveRuleResult(rrId), 'Exception resolved.');
  const handleOverride = (rrId, reason) => runMutation(() => paymentsApi.overrideRuleResult(rrId, reason), 'Exception overridden.');
  const handleRunRules = () => runMutation(() => paymentsApi.runRules(id), 'Rules re-run and batch recalculated.');
  const handleSaveNotes = async () => {
    try { await paymentsApi.patchBatch(id, { notes }); notify('Notes saved.'); }
    catch (e) { notify(e.message, 'error'); }
  };
  const handleSubmitConfirm = async () => {
    await paymentsApi.submitToFinance(id);
    setSubmitOpen(false);
    await loadBatch();
    await loadRows();
    notify('Batch submitted to Finance.');
  };

  // ---- Finance mutations ----
  const handleBeginReview = () => runMutation(() => paymentsApi.beginReview(id), 'Review started.');
  const handleApproveConfirm = async () => {
    await paymentsApi.approveBatch(id); // throws (422) -> dialog shows blockers
    setApproveOpen(false);
    await loadRows();
    await refreshDerived();
    notify('Batch approved.');
  };
  const handleRowFinanceApprove = (row) => runMutation(() => paymentsApi.approveRow(row.id), 'Row approved.');
  const handleProviderApprove = (providerId) => runMutation(() => paymentsApi.approveProvider(id, providerId), 'Provider approved.');
  const doAddAdjustment = (body) =>
    runMutation(async () => { await paymentsApi.addAdjustment(id, body); setAdjustmentOpen(false); }, 'Adjustment added.');
  const doDeleteAdjustment = (adjId) => runMutation(() => paymentsApi.deleteAdjustment(id, adjId), 'Adjustment removed.');

  // ---- Statements (Phase 6) ----
  const handleGenerateStatements = async () => {
    setBusy(true);
    try {
      await paymentsApi.generateStatements(id);
      await loadStatements();
      notify('Statements generated.');
    } catch (e) {
      notify(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };
  const handlePreviewStatement = async (stmtId) => {
    try {
      const url = await paymentsApi.previewStatement(stmtId);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      notify(e.message, 'error');
    }
  };
  const emailAction = async (fn, okMsg) => {
    setBusy(true);
    try {
      const r = await fn();
      await loadStatements();
      await loadBatch();
      return r;
    } catch (e) {
      notify(e.message, 'error');
      return null;
    } finally {
      setBusy(false);
    }
  };
  const handleApproveEmails = async () => {
    const r = await emailAction(() => paymentsApi.approveEmails(id));
    if (!r) return;
    const blocked = (r.results || []).filter((x) => !x.ok);
    if (blocked.length === 0) notify(`Approved ${r.approvedCount} statement email(s).`);
    else notify(`${r.approvedCount} approved; ${blocked.length} blocked — e.g. ${blocked[0].statement_number}: ${blocked[0].reasons[0]}`, 'warning');
  };
  const handleSendEmails = async () => {
    const r = await emailAction(() => paymentsApi.sendEmails(id));
    if (r) notify(`Sent ${r.sent}${r.failed ? `, ${r.failed} failed` : ''}.`, r.failed ? 'warning' : 'success');
  };
  const handleSendTest = async () => {
    const r = await emailAction(() => paymentsApi.sendTestEmail(id));
    if (r) notify(`Test email sent to ${r.to}.`);
  };
  const handleSendStatement = async (stmtId) => {
    const r = await emailAction(() => paymentsApi.sendStatement(stmtId));
    if (r) notify('Statement sent.');
  };
  const handleResendStatement = async (stmtId) => {
    const r = await emailAction(() => paymentsApi.resendStatement(stmtId));
    if (r) notify('Statement resent.');
  };

  // ---- QBO export (Phase 6 PR3) ----
  const handleGenerateExport = async () => {
    if (qboExports.length > 0) {
      // Re-export requires a reason.
      setDecision({ kind: 'qbo-reexport', title: 'Re-export QBO CSV', description: 'This creates a new versioned export.', confirmLabel: 'Re-export', confirmColor: 'primary' });
      return;
    }
    setBusy(true);
    try {
      const r = await paymentsApi.generateQboExport(id);
      await loadQboExports();
      await loadBatch();
      notify(r.unmappedVendors > 0 ? `Export generated. ${r.unmappedVendors} row(s) have no QBO vendor ID.` : 'Export generated.', r.unmappedVendors > 0 ? 'warning' : 'success');
    } catch (e) {
      notify(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };
  const handleDownloadExport = async (exportId) => {
    try {
      const url = await paymentsApi.downloadQboExport(exportId);
      window.open(url, '_blank', 'noopener');
      await loadQboExports();
    } catch (e) {
      notify(e.message, 'error');
    }
  };
  const handleCloseBatch = () => runMutation(() => paymentsApi.closeBatch(id), 'Batch closed.');

  const onDecisionConfirm = (reason) =>
    runMutation(async () => {
      const k = decision.kind;
      if (k === 'batch-reject') await paymentsApi.rejectBatch(id, reason);
      else if (k === 'batch-return') await paymentsApi.returnBatch(id, reason);
      else if (k === 'row-reject') await paymentsApi.rejectRow(decision.target, reason);
      else if (k === 'provider-reject') await paymentsApi.rejectProvider(id, decision.target, reason);
      else if (k === 'qbo-reexport') { await paymentsApi.generateQboExport(id, reason); await loadQboExports(); }
      setDecision(null);
    }, 'Done.');

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }
  if (!batch) {
    return <Box sx={{ mt: 3 }}><Alert severity="error">Batch not found.</Alert></Box>;
  }

  const openExceptions = ruleResults.filter((r) => r.status === 'open').length;

  return (
    <Box sx={{ mt: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/payments/batches')}>Batches</Button>
          <Typography variant="h4">{batch.batch_number}</Typography>
          <BatchStatusChip status={batch.status} size="medium" />
        </Box>

        {/* Operations actions */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button variant="outlined" startIcon={<RuleIcon />} onClick={handleRunRules} disabled={!editable || busy}>Run rules</Button>
          <Button variant="contained" startIcon={<SendIcon />} onClick={() => setSubmitOpen(true)} disabled={!editable || busy}>Submit to Finance</Button>
          {!editable && !financeActionable && (
            <Alert severity="info" sx={{ py: 0 }}>This batch is locked from edits ({batch.status.replace(/_/g, ' ')}).</Alert>
          )}
        </Box>

        {/* Finance actions */}
        {financeActionable && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center', p: 1.5, bgcolor: 'rgba(25,118,210,0.06)', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mr: 1 }}>Finance:</Typography>
            {batch.status === 'submitted_to_finance' && (
              <Button size="small" variant="outlined" onClick={handleBeginReview} disabled={busy}>Begin review</Button>
            )}
            <Button size="small" variant="contained" color="success" startIcon={<CheckCircleIcon />} onClick={() => setApproveOpen(true)} disabled={busy}>Approve batch</Button>
            <Button size="small" variant="outlined" color="error" startIcon={<CancelIcon />} onClick={() => setDecision({ kind: 'batch-reject', title: 'Reject batch', description: 'Reject the entire batch.', confirmLabel: 'Reject batch', confirmColor: 'error' })} disabled={busy}>Reject</Button>
            <Button size="small" variant="outlined" color="warning" startIcon={<ReplayIcon />} onClick={() => setDecision({ kind: 'batch-return', title: 'Return to Operations', description: 'Send the batch back to Operations. Finance adjustments and row decisions will be cleared.', confirmLabel: 'Return', confirmColor: 'warning' })} disabled={busy}>Return to Ops</Button>
          </Box>
        )}

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }} variant="scrollable" scrollButtons="auto">
          <Tab label="Summary" />
          <Tab label={`Rows (${batch.total_rows || rows.length})`} />
          <Tab label={`Exceptions${openExceptions ? ` (${openExceptions})` : ''}`} />
          <Tab label="Provider Totals" />
          <Tab label={`Adjustments${adjustments.length ? ` (${adjustments.length})` : ''}`} />
          <Tab label={`Statements${statements.length ? ` (${statements.length})` : ''}`} />
          <Tab label="QBO" />
          <Tab label="Audit" />
        </Tabs>

        {/* Summary */}
        {tab === 0 && (
          <Box>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={3} md={2}><StatCard label="Total rows" value={batch.total_rows || 0} /></Grid>
              <Grid item xs={6} sm={3} md={2}><StatCard label="Payable" value={batch.payable_rows || 0} /></Grid>
              <Grid item xs={6} sm={3} md={2}><StatCard label="Excluded" value={batch.excluded_rows || 0} /></Grid>
              <Grid item xs={6} sm={3} md={2}><StatCard label="Flagged" value={batch.flagged_rows || 0} /></Grid>
              <Grid item xs={6} sm={3} md={2}><StatCard label="Calculated" value={money(batch.total_calculated_amount)} /></Grid>
              <Grid item xs={6} sm={3} md={2}><StatCard label="Approved" value={money(batch.total_approved_amount)} /></Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}><Typography variant="body2"><strong>Invoice week:</strong> {batch.invoice_week || '—'}</Typography></Grid>
              <Grid item xs={12} sm={6} md={3}><Typography variant="body2"><strong>Source:</strong> {batch.order_source || '—'}</Typography></Grid>
              <Grid item xs={12} sm={6} md={3}><Typography variant="body2"><strong>File:</strong> {batch.uploaded_file_name || '—'}</Typography></Grid>
              <Grid item xs={12} sm={6} md={3}><Typography variant="body2"><strong>Uploaded:</strong> {batch.uploaded_at ? new Date(batch.uploaded_at).toLocaleString() : '—'}</Typography></Grid>
            </Grid>
            <Box sx={{ mt: 3, maxWidth: 600 }}>
              <TextField label="Batch notes" value={notes} onChange={(e) => setNotes(e.target.value)} multiline rows={3} fullWidth disabled={!editable} />
              <Button sx={{ mt: 1 }} onClick={handleSaveNotes} disabled={!editable}>Save notes</Button>
            </Box>
          </Box>
        )}

        {/* Rows */}
        {tab === 1 && (
          <ReviewGrid
            rows={rows}
            loading={busy}
            editable={editable}
            financeMode={financeActionable}
            onCommitEdit={handleCommitEdit}
            onProcessRowUpdateError={(e) => notify(e.message, 'error')}
            onExclude={(row) => setExcludeTarget(row)}
            onInclude={handleInclude}
            onMapProvider={(row) => setMapping({ type: 'provider', row })}
            onMapService={(row) => setMapping({ type: 'service', row })}
            onFinanceApprove={handleRowFinanceApprove}
            onFinanceReject={(row) => setDecision({ kind: 'row-reject', target: row.id, title: `Reject row #${row.source_row_number}`, confirmLabel: 'Reject row', confirmColor: 'error' })}
            selectionModel={selection}
            onSelectionModelChange={setSelection}
          />
        )}

        {/* Exceptions */}
        {tab === 2 && (
          <ExceptionQueue ruleResults={ruleResults} busy={busy} onResolve={handleResolve} onOverride={handleOverride} onJumpToRow={() => setTab(1)} />
        )}

        {/* Provider Totals */}
        {tab === 3 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Provider</TableCell>
                <TableCell>Mapping</TableCell>
                <TableCell align="right">Rows</TableCell>
                <TableCell align="right">Payable</TableCell>
                <TableCell align="right">Excluded</TableCell>
                <TableCell align="right">Flagged</TableCell>
                <TableCell align="right">Calculated</TableCell>
                {financeActionable && <TableCell align="right">Finance</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {providerTotals.map((g, i) => (
                <TableRow key={g.provider_id || `unmatched-${i}`}>
                  <TableCell>{g.provider_name}</TableCell>
                  <TableCell>{g.mapping_status || '—'}</TableCell>
                  <TableCell align="right">{g.total_rows}</TableCell>
                  <TableCell align="right">{g.payable_rows}</TableCell>
                  <TableCell align="right">{g.excluded_rows}</TableCell>
                  <TableCell align="right">{g.flagged_rows}</TableCell>
                  <TableCell align="right">{money(g.calculated_amount)}</TableCell>
                  {financeActionable && (
                    <TableCell align="right">
                      {g.provider_id ? (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Button size="small" color="success" disabled={busy} onClick={() => handleProviderApprove(g.provider_id)}>Approve</Button>
                          <Button size="small" color="error" disabled={busy} onClick={() => setDecision({ kind: 'provider-reject', target: g.provider_id, title: `Reject provider: ${g.provider_name}`, confirmLabel: 'Reject provider', confirmColor: 'error' })}>Reject</Button>
                        </Box>
                      ) : '—'}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Adjustments */}
        {tab === 4 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1">Manual adjustments</Typography>
              {financeActionable && <Button variant="outlined" size="small" onClick={() => setAdjustmentOpen(true)} disabled={busy}>Add adjustment</Button>}
            </Box>
            {adjustments.length === 0 ? (
              <Alert severity="info">No adjustments on this batch.</Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Scope</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>When</TableCell>
                    {financeActionable && <TableCell />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {adjustments.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>{a.payment_row_id ? 'Row' : a.provider_id ? 'Provider' : 'Batch'}</TableCell>
                      <TableCell><Chip size="small" label={a.adjustment_type} /></TableCell>
                      <TableCell align="right">{money(a.amount)}</TableCell>
                      <TableCell>{a.reason}</TableCell>
                      <TableCell>{a.entered_at ? new Date(a.entered_at).toLocaleString() : '—'}</TableCell>
                      {financeActionable && (
                        <TableCell align="right">
                          <IconButton size="small" color="error" disabled={busy} onClick={() => doDeleteAdjustment(a.id)}><DeleteIcon fontSize="small" /></IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        )}

        {/* Statements (Phase 6) */}
        {tab === 5 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle1">Contractor statements</Typography>
              {canGenerateStatements && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button variant="outlined" size="small" onClick={handleGenerateStatements} disabled={busy}>
                    {statements.length ? 'Regenerate' : 'Generate'}
                  </Button>
                  {statements.length > 0 && (
                    <>
                      <Button variant="outlined" size="small" onClick={handleApproveEmails} disabled={busy}>Approve emails</Button>
                      <Button variant="contained" size="small" onClick={handleSendEmails} disabled={busy}>Send all approved</Button>
                      <Button size="small" onClick={handleSendTest} disabled={busy}>Send test to me</Button>
                    </>
                  )}
                </Box>
              )}
            </Box>
            {!approvedOrLater ? (
              <Alert severity="info">Statements become available after Finance approves the batch.</Alert>
            ) : statements.length === 0 ? (
              <Alert severity="info">No statements generated yet{canGenerateStatements ? ' — click “Generate statements”.' : '.'}</Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Statement</TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statements.map((st) => (
                    <TableRow key={st.id}>
                      <TableCell>{st.statement_number}</TableCell>
                      <TableCell>{st.providers?.name || '—'}</TableCell>
                      <TableCell align="right">{money(st.total_amount)}</TableCell>
                      <TableCell><Chip size="small" label={st.statement_status} /></TableCell>
                      <TableCell><Chip size="small" variant="outlined" label={st.email_status || 'not_generated'} /></TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Button size="small" onClick={() => handlePreviewStatement(st.id)}>Preview</Button>
                          {canGenerateStatements && st.email_status === 'approved' && (
                            <Button size="small" variant="outlined" disabled={busy} onClick={() => handleSendStatement(st.id)}>Send</Button>
                          )}
                          {canGenerateStatements && ['sent', 'failed', 'resent'].includes(st.email_status) && (
                            <Button size="small" disabled={busy} onClick={() => handleResendStatement(st.id)}>Resend</Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        )}
        {/* QBO export (Phase 6) */}
        {tab === 6 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle1">QuickBooks export</Typography>
              {canGenerateStatements && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" size="small" onClick={handleGenerateExport} disabled={busy}>
                    {qboExports.length ? 'Re-export' : 'Generate export'}
                  </Button>
                  {batch.status !== 'closed' && (
                    <Button size="small" color="inherit" onClick={handleCloseBatch} disabled={busy}>Close batch</Button>
                  )}
                </Box>
              )}
            </Box>
            {!approvedOrLater ? (
              <Alert severity="info">QBO export becomes available after Finance approves the batch.</Alert>
            ) : qboExports.length === 0 ? (
              <Alert severity="info">No export generated yet{canGenerateStatements ? ' — click “Generate export”.' : '.'}</Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Export</TableCell>
                    <TableCell align="right">Rows</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Generated</TableCell>
                    <TableCell>Re-export reason</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {qboExports.map((x) => (
                    <TableRow key={x.id}>
                      <TableCell>{x.export_number}</TableCell>
                      <TableCell align="right">{x.total_rows}</TableCell>
                      <TableCell align="right">{money(x.total_amount)}</TableCell>
                      <TableCell><Chip size="small" label={x.export_status} /></TableCell>
                      <TableCell>{x.generated_at ? new Date(x.generated_at).toLocaleString() : '—'}</TableCell>
                      <TableCell>{x.reexport_reason || '—'}</TableCell>
                      <TableCell align="right"><Button size="small" onClick={() => handleDownloadExport(x.id)}>Download CSV</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        )}

        {/* Audit + approvals */}
        {tab === 7 && (
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Finance decisions</Typography>
            {approvals.length === 0 ? (
              <Alert severity="info" sx={{ mb: 3 }}>No approval/rejection history yet.</Alert>
            ) : (
              <Table size="small" sx={{ mb: 3 }}>
                <TableHead>
                  <TableRow><TableCell>When</TableCell><TableCell>Scope</TableCell><TableCell>Decision</TableCell><TableCell>Reason</TableCell></TableRow>
                </TableHead>
                <TableBody>
                  {approvals.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>{new Date(a.acted_at).toLocaleString()}</TableCell>
                      <TableCell>{a.scope}{a.scope_ref ? ` (${String(a.scope_ref).slice(0, 8)}…)` : ''}</TableCell>
                      <TableCell><Chip size="small" label={a.status} color={a.status === 'approved' ? 'success' : a.status === 'rejected' ? 'error' : 'warning'} /></TableCell>
                      <TableCell>{a.comments || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Audit events</Typography>
            <Button sx={{ mb: 1 }} size="small" onClick={loadAudit}>Load audit events</Button>
            {auditEvents.length === 0 ? (
              <Alert severity="info">No audit events loaded.</Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow><TableCell>When</TableCell><TableCell>Event</TableCell><TableCell>Entity</TableCell><TableCell>User</TableCell><TableCell>Reason</TableCell></TableRow>
                </TableHead>
                <TableBody>
                  {auditEvents.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell>{new Date(e.created_at).toLocaleString()}</TableCell>
                      <TableCell>{e.event_type}</TableCell>
                      <TableCell>{e.entity_type}</TableCell>
                      <TableCell>{e.user_name || '—'}</TableCell>
                      <TableCell>{e.reason || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        )}
      </Paper>

      <ExcludeRowDialog open={!!excludeTarget} row={excludeTarget} submitting={busy} onClose={() => setExcludeTarget(null)} onConfirm={doExclude} />
      <MappingDialog
        open={!!mapping}
        type={mapping?.type}
        uploadedName={mapping?.type === 'provider' ? mapping?.row?.uploaded_provider_name : mapping?.row?.uploaded_service_type}
        submitting={busy}
        onClose={() => setMapping(null)}
        onConfirm={doMapping}
      />
      <SubmitToFinanceDialog open={submitOpen} batch={batch} onClose={() => setSubmitOpen(false)} onConfirm={handleSubmitConfirm} />
      <FinanceApproveDialog open={approveOpen} batch={batch} onClose={() => setApproveOpen(false)} onConfirm={handleApproveConfirm} />
      <FinanceDecisionDialog
        open={!!decision}
        title={decision?.title}
        description={decision?.description}
        confirmLabel={decision?.confirmLabel}
        confirmColor={decision?.confirmColor}
        submitting={busy}
        onClose={() => setDecision(null)}
        onConfirm={onDecisionConfirm}
      />
      <AdjustmentDialog open={adjustmentOpen} rows={rows} providerTotals={providerTotals} submitting={busy} onClose={() => setAdjustmentOpen(false)} onConfirm={doAddAdjustment} />

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default BatchDetail;
